import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

// Create context
const AiPageContext = createContext(null);

// Default loading text configuration
export const DEFAULT_LOADING_TEXTS = [
  {
    text: "Querying data from your organization",
    showOnce: true,
    minTime: 2000,
    maxTime: 3000
  },
  {
    text: "Thinking",
    showOnce: false,
    minTime: 1500,
    maxTime: 2500
  },
  {
    text: "Looking through the information",
    showOnce: false,
    minTime: 2000,
    maxTime: 4000
  },
  {
    text: "Querying more data",
    showOnce: false,
    minTime: 2500,
    maxTime: 3500
  }
];

/**
 * Helper to check if two contexts are equivalent
 * @param {Object} context1 - First context object 
 * @param {Object} context2 - Second context object
 * @returns {boolean} - Whether they are equivalent
 */
const areContextsEqual = (context1, context2) => {
  // If both are null/undefined, they're equal
  if (!context1 && !context2) return true;
  // If only one is null/undefined, they're not equal
  if (!context1 || !context2) return false;
  
  // Compare type and value which are the primary identifiers
  return (
    context1.type === context2.type && 
    context1.label === context2.label
  );
};

/**
 * Provider component that manages and provides page context for the AI Command Palette
 */
export const AiPageContextProvider = ({ children }) => {
  const [currentAiPageContext, setCurrentAiPageContext] = useState(null);
  const location = useLocation();

  // Reset context on location change - individual pages will set their specific context
  useEffect(() => {
    // Don't reset on a location.state change (i.e. when details are updated dynamically)
    setCurrentAiPageContext(null);
  }, [location.pathname]);

  // Enhanced setContext function that handles loading text configuration
  const setContext = useCallback((context) => {
    // Check if the context is actually different before updating
    if (!areContextsEqual(context, currentAiPageContext)) {
      // Ensure loading texts use the default if not provided
      if (context && !context.loadingTexts) {
        context.loadingTexts = DEFAULT_LOADING_TEXTS;
      }
      
      console.log('Setting new context:', context?.type, context?.label);
      setCurrentAiPageContext(context);
    }
  }, [currentAiPageContext]);

  return (
    <AiPageContext.Provider value={{ currentAiPageContext, setCurrentAiPageContext: setContext }}>
      {children}
    </AiPageContext.Provider>
  );
};

/**
 * Hook to use the page context
 */
export const useAiPageContext = () => {
  const context = useContext(AiPageContext);
  if (!context) {
    throw new Error('useAiPageContext must be used within a AiPageContextProvider');
  }
  return context;
}; 